import { useEffect } from "react"
import { useApolloClient } from "@apollo/client"
import config from "@/v2-backoffice-client/config"
import {
  idToken,
  acquireTokenSilent,
} from "@/v2-backoffice-client/auth/auth.instance"
import unreadCountQuery from "@/v2-backoffice-client/notification/unreadCount/notification.unreadCount.query.gql"
import type { StaffNotificationUnreadCountQuery } from "@/v2-backoffice-client/notification/unreadCount/__types__/notification.unreadCount.query"
import {
  NOTIFICATION_STREAM_RECONNECT_MIN_MS,
  NOTIFICATION_STREAM_RECONNECT_MAX_MS,
} from "@/v2-backoffice-client/notification/notification.constants"

/** Mirrors the server's notification.FeedState (SSE `feed` events). */
type StaffNotificationFeedState = {
  unreadCount: number
  latestId: number
}

// The SSE endpoint lives beside /graphql on the backoffice API.
function notificationEventsUrl() {
  return config.graphqlUrl.replace(/\/graphql$/, "") + "/events/notifications"
}

/**
 * Live staff-notification updates over server-sent events — replaces
 * client polling. The server pushes a tiny `{unreadCount, latestId}`
 * snapshot whenever the feed changes; the badge updates straight from
 * the payload (written into the Apollo cache) and the feed list — when
 * mounted — is refetched through the normal query.
 *
 * Native `EventSource` cannot send an Authorization header, so the
 * stream is read from a plain `fetch` body. Reconnects with backoff on
 * any error and acquires a fresh token per connect (the server ends
 * streams every ~10 minutes precisely so tokens get re-validated).
 *
 * Mount exactly once, in the always-rendered notification bell.
 */
export function useStaffNotificationStream() {
  const client = useApolloClient()

  useEffect(() => {
    let isStopped = false
    let controller: AbortController | null = null
    let reconnectTimer: number | undefined
    let reconnectDelay = NOTIFICATION_STREAM_RECONNECT_MIN_MS
    let lastAppliedState: StaffNotificationFeedState | null = null

    function applyFeedState(state: StaffNotificationFeedState) {
      const isChanged =
        lastAppliedState === null ||
        lastAppliedState.unreadCount !== state.unreadCount ||
        lastAppliedState.latestId !== state.latestId

      client.writeQuery<StaffNotificationUnreadCountQuery>({
        query: unreadCountQuery,
        data: { getStaffNotificationUnreadCount: state.unreadCount },
      })

      // Refetch rows only when something actually changed since the
      // last applied state — the connect-time snapshot alone shouldn't
      // re-run the feed query right after its initial fetch.
      if (lastAppliedState !== null && isChanged) {
        client
          .refetchQueries({ include: ["StaffNotificationsQuery"] })
          .catch(() => {
            // Inactive/failed refetch is fine — the next open of the
            // panel fetches fresh via cache-and-network.
          })
      }
      lastAppliedState = state
    }

    function handleRawEvent(rawEvent: string) {
      let eventName = "message"
      const dataLines: string[] = []
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) {
          eventName = line.slice("event:".length).trim()
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice("data:".length).trim())
        }
      }
      if (eventName !== "feed" || dataLines.length === 0) return
      try {
        applyFeedState(
          JSON.parse(dataLines.join("\n")) as StaffNotificationFeedState,
        )
      } catch {
        // Malformed event — skip it; the next snapshot self-heals.
      }
    }

    async function connect() {
      if (isStopped) return
      controller = new AbortController()
      try {
        let token = idToken
        try {
          const response = await acquireTokenSilent()
          token = response.idToken
        } catch {
          // Silent refresh failed — try the cached token; a 401 lands
          // in the reconnect path below.
        }

        const response = await fetch(notificationEventsUrl(), {
          headers: {
            authorization: token ? `Bearer ${token}` : "",
            accept: "text/event-stream",
          },
          signal: controller.signal,
        })
        if (!response.ok || !response.body) {
          throw new Error(`notification stream responded ${response.status}`)
        }
        reconnectDelay = NOTIFICATION_STREAM_RECONNECT_MIN_MS

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader()
        let buffer = ""
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += value
          const rawEvents = buffer.split("\n\n")
          buffer = rawEvents.pop() ?? ""
          for (const rawEvent of rawEvents) handleRawEvent(rawEvent)
        }
      } catch {
        // Aborted, offline, 401, … — every path ends in the same
        // reconnect below.
      }
      if (isStopped) return
      reconnectTimer = window.setTimeout(connect, reconnectDelay)
      reconnectDelay = Math.min(
        reconnectDelay * 2,
        NOTIFICATION_STREAM_RECONNECT_MAX_MS,
      )
    }

    connect()

    return () => {
      isStopped = true
      window.clearTimeout(reconnectTimer)
      controller?.abort()
    }
  }, [client])
}
